import React, { useState, useEffect, useMemo } from 'react';
import PayLaterBanner from 'components/PayLaterBanner';
import {
    EKey,
    getHasClosePayLaterBannerInDashboard,
    getHasCloseZoomVideoVisitsBannerInDashboard,
    setHasCloseZoomVideoVisitsBannerInDashboard,
} from 'utils/localstore';
import s from './s.module.less';
import ZoomVideoVisitsBanner from 'components/ZoomVideoVisitsBanner';
import AskChartBanner from 'components/AskChartBanner';
import JoinVerifyPlusBanner from 'components/JoinVerifyPlusBanner';

type BannerType = 'joinVerifyPlus' | 'practiceListing' | 'payLater' | 'zoomVideoVisits' | 'askChart';

type Props = {
    /** Practice Listing Banner 的渲染内容 */
    practiceListingBanner?: React.ReactNode;
    /** 是否显示 Practice Listing Banner */
    showPracticeListingBanner?: boolean;
    /** 是否显示 Pay Later Banner */
    showPayLaterBanner?: boolean;
    /** 是否显示 Zoom Video Visits Banner */
    showZoomVideoVisitsBanner?: boolean;
    /** 是否显示 AskChart Banner */
    showAskChartBanner?: boolean;
    /** 是否显示 Join Verify Plus Banner */
    showJoinVerifyPlus?: boolean;
    /** Practice Listing Banner 关闭回调 */
    onPracticeListingBannerClose?: () => void;
};

/**
 * Banner 轮播组件
 * 支持多个 banner 的切换显示，每个 banner 可以独立关闭
 */
const DISMISS_KEY_JOIN_VERIFY_PLUS = 'klarity_join_verify_plus_banner_dismissed';

const BannerCarousel: React.FC<Props> = ({
    practiceListingBanner,
    showPracticeListingBanner = false,
    showPayLaterBanner = false,
    showZoomVideoVisitsBanner = false,
    showAskChartBanner = false,
    showJoinVerifyPlus = false,
    onPracticeListingBannerClose,
}) => {
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [isPracticeListingBannerClosed, setIsPracticeListingBannerClosed] = useState(false);
    const [isPayLaterBannerClosed, setIsPayLaterBannerClosed] = useState(getHasClosePayLaterBannerInDashboard());
    const [isZoomVideoVisitsBannerClosed, setIsZoomVideoVisitsBannerClosed] = useState(
        getHasCloseZoomVideoVisitsBannerInDashboard(),
    );
    const [isAskChartBannerClosed, setIsAskChartBannerClosed] = useState(
        () => localStorage.getItem('klarity_insurance_billing_banner_dismissed') === '1',
    );
    const [isJoinVerifyPlusBannerClosed, setIsJoinVerifyPlusBannerClosed] = useState(
        () => localStorage.getItem(DISMISS_KEY_JOIN_VERIFY_PLUS) === '1',
    );
    // 获取可用的 banner 列表
    const availableBanners = useMemo(() => {
        const banners: BannerType[] = [];
        if (showJoinVerifyPlus && !isJoinVerifyPlusBannerClosed) {
            banners.push('joinVerifyPlus');
        }
        if (showAskChartBanner && !isAskChartBannerClosed) {
            banners.push('askChart');
        }
        console.log('isPracticeListingBannerClosed: ', isPracticeListingBannerClosed);

        if (showPracticeListingBanner && practiceListingBanner && !isPracticeListingBannerClosed) {
            banners.push('practiceListing');
        }
        if (showZoomVideoVisitsBanner && !isZoomVideoVisitsBannerClosed) {
            banners.push('zoomVideoVisits');
        }
        if (showPayLaterBanner && !isPayLaterBannerClosed) {
            banners.push('payLater');
        }
        return banners;
    }, [
        showJoinVerifyPlus,
        isJoinVerifyPlusBannerClosed,
        showPracticeListingBanner,
        practiceListingBanner,
        isPracticeListingBannerClosed,
        showPayLaterBanner,
        isPayLaterBannerClosed,
        showZoomVideoVisitsBanner,
        isZoomVideoVisitsBannerClosed,
        showAskChartBanner,
        isAskChartBannerClosed,
    ]);

    // 当前显示的 banner
    const currentBanner = availableBanners[currentBannerIndex];

    // 监听 PayLaterBanner 的关闭状态
    useEffect(() => {
        const checkPayLaterBannerClosed = () => {
            const closed = getHasClosePayLaterBannerInDashboard();
            if (closed && !isPayLaterBannerClosed) {
                setIsPayLaterBannerClosed(true);
                // 如果当前显示的是被关闭的 banner，切换到下一个
                if (currentBanner === 'payLater' && availableBanners.length > 1) {
                    const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
                    setCurrentBannerIndex(nextIndex);
                }
            }
        };
        // 定期检查 localStorage 的变化
        const interval = setInterval(checkPayLaterBannerClosed, 200);
        return () => clearInterval(interval);
    }, [isPayLaterBannerClosed, currentBanner, availableBanners.length, currentBannerIndex]);

    // 当 availableBanners 变化时，调整 currentBannerIndex
    useEffect(() => {
        if (availableBanners.length > 0 && currentBannerIndex >= availableBanners.length) {
            setCurrentBannerIndex(0);
        }
    }, [availableBanners.length, currentBannerIndex]);

    // 切换到下一个 banner
    const handleNextBanner = () => {
        setCurrentBannerIndex((prev) => (prev + 1) % availableBanners.length);
    };

    // 处理 PracticeListingBanner 关闭
    const handlePracticeListingBannerClose = () => {
        setIsPracticeListingBannerClosed(true);
        // 调用外部回调
        onPracticeListingBannerClose?.();
        // 如果当前显示的是被关闭的 banner，切换到下一个
        if (currentBanner === 'practiceListing' && availableBanners.length > 1) {
            const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
            setCurrentBannerIndex(nextIndex);
        }
    };

    // 处理 PayLaterBanner 关闭
    const handlePayLaterBannerClose = () => {
        setIsPayLaterBannerClosed(true);
        // 如果当前显示的是被关闭的 banner，切换到下一个
        if (currentBanner === 'payLater' && availableBanners.length > 1) {
            const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
            setCurrentBannerIndex(nextIndex);
        }
    };

    const handleZoomVideoVisitsBannerClose = () => {
        setIsZoomVideoVisitsBannerClosed(true);
        setHasCloseZoomVideoVisitsBannerInDashboard(true);
        // 如果当前显示的是被关闭的 banner，切换到下一个
        if (currentBanner === 'zoomVideoVisits' && availableBanners.length > 1) {
            const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
            setCurrentBannerIndex(nextIndex);
        }
    };

    // 如果没有可用的 banner，不渲染
    if (availableBanners.length === 0) {
        return null;
    }

    const handleJoinVerifyPlusBannerClose = () => {
        setIsJoinVerifyPlusBannerClosed(true);
        localStorage.setItem(DISMISS_KEY_JOIN_VERIFY_PLUS, '1');
        if (currentBanner === 'joinVerifyPlus' && availableBanners.length > 1) {
            const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
            setCurrentBannerIndex(nextIndex);
        }
    };

    return (
        <div className={s.bannerContainer}>
            {currentBanner === 'joinVerifyPlus' && (
                <div className={s.bannerWrapper}>
                    <JoinVerifyPlusBanner onClose={handleJoinVerifyPlusBannerClose} />
                </div>
            )}
            {currentBanner === 'practiceListing' && practiceListingBanner && (
                <div className={s.bannerWrapper}>{practiceListingBanner}</div>
            )}
            {currentBanner === 'zoomVideoVisits' && (
                <div className={s.bannerWrapper}>
                    <ZoomVideoVisitsBanner onClose={handleZoomVideoVisitsBannerClose} />
                </div>
            )}
            {currentBanner === 'payLater' && (
                <div className={s.bannerWrapper}>
                    <PayLaterBanner closeMarkKey={EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_DASHBOARD} />
                </div>
            )}
            {currentBanner === 'askChart' && (
                <div className={s.bannerWrapper}>
                    <AskChartBanner
                        onClose={() => {
                            setIsAskChartBannerClosed(true);
                            if (availableBanners.length > 1) {
                                const nextIndex = (currentBannerIndex + 1) % availableBanners.length;
                                setCurrentBannerIndex(nextIndex);
                            }
                        }}
                    />
                </div>
            )}
            {/* 如果有多个 banner，显示切换按钮 */}
            {availableBanners.length > 1 && (
                <div className={s.bannerSwitchButton} onClick={handleNextBanner}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                        <circle cx="9.5" cy="9.5" r="9.5" fill="white" fillOpacity="0.9" />
                        <path
                            d="M7 5L12 9.5L7 14"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            )}
        </div>
    );
};

export default BannerCarousel;
